import { DEFAULT_CLIENT } from '@boozt/constants';
import type { AppPropsWithChildren, ComponentProps, ResponseContext } from '@boozt/types';
import getStoreInstance from '@shopboozt/store';
import { Provider } from 'react-redux';
import { isServerSide } from 'helpers/ssr';
import { PaletteConfigProvider } from 'palette/Config';
import { ModalContextProvider } from 'palette/Modal';
import { ToastContextProvider } from 'palette/Toast';
import { ConfigProvider } from '@common/Config';
import { TranslationsProvider, useTranslate } from '@common/Translations';
import { getStoreEnv } from '@helpers/env';

type ProviderProps = AppPropsWithChildren & {
    initialState: any;
};

let cachedStoreInstance: any = null;

function initializeStore(initialState: any): any {
    if (cachedStoreInstance !== null && !isServerSide()) {
        return cachedStoreInstance;
    }

    cachedStoreInstance = getStoreInstance(initialState);
    return cachedStoreInstance;
}

function PaletteConfigSection({ context, children }: ComponentProps<{ config?: any }, ResponseContext> & {
    children?: React.ReactNode;
}) {
    const { SVGURL, device = DEFAULT_CLIENT } = context || {
        SVGURL: '',
        device: DEFAULT_CLIENT,
    };
    const translate = useTranslate();
    const theme = getStoreEnv(context);

    return (
        <PaletteConfigProvider
            device={device}
            logoPath={SVGURL}
            svgPath={SVGURL}
            theme={theme}
            translate={translate}
        >
            <ModalContextProvider>
                <ToastContextProvider>{children}</ToastContextProvider>
            </ModalContextProvider>
        </PaletteConfigProvider>
    );
}

export default function App(app: ProviderProps) {
    const {
        initialState, children, context, props,
    } = app;

    return (
        <Provider store={initializeStore(initialState)}>
            <ConfigProvider
                config={props.config}
                context={context}
            >
                <TranslationsProvider context={context}>
                    <PaletteConfigSection
                        context={context}
                        props={props}
                    >
                        {children}
                    </PaletteConfigSection>
                </TranslationsProvider>
            </ConfigProvider>
        </Provider>
    );
}
